/* ─────────────────────────────────────────────────────────
 *  TableOps – useTableOpsSoundCues (store observer)
 *
 *  Headless observer that watches the OrderStore and fires audio
 *  cues on the two lifecycle edges the operator cares about:
 *
 *    • order-enter   → a brand-new order appears in the store.
 *    • station-close → a ticket is bumped to `ready` by its station
 *                      (kitchen / pastry / cafeteria / bar finishing it).
 *
 *  Why an observer (not callbacks wired into the store)? Orders enter
 *  through several paths (manual dispatch, SSE bridge, hydration). A
 *  single diffing observer keeps the reducer pure and the components
 *  pure — they never know audio exists.
 *
 *  Burst-safe: the first run seeds a silent baseline, and a multi-order
 *  delta (hydration / tenant switch) is treated as a bulk reload, not a
 *  stream of live arrivals, so it never machine-guns the speakers.
 * ───────────────────────────────────────────────────────── */
import { useEffect, useRef } from 'react'
import { useOrderStore } from '../useOrderStore'
import type { TableOrder } from '../orderTypes'
import { useTableOpsSounds } from './useTableOpsSounds'

interface CueCallbacks {
  playOrderEnter: () => void
  playOrderComplete: () => void
}

export function useTableOpsSoundCues(externalCues?: CueCallbacks): void {
  const { state } = useOrderStore()
  const sounds = useTableOpsSounds()
  const playOrderEnter = externalCues?.playOrderEnter ?? sounds.playOrderEnter
  const playOrderComplete = externalCues?.playOrderComplete ?? sounds.playOrderComplete

  const seeded = useRef(false)
  const knownOrders = useRef<Set<string>>(new Set())
  const orderStatus = useRef<Map<string, TableOrder['status']>>(new Map())

  useEffect(() => {
    const orders: TableOrder[] = []
    for (const list of Object.values(state.tables)) {
      for (const order of list) orders.push(order)
    }

    // First pass — record the baseline without firing any cue.
    if (!seeded.current) {
      for (const order of orders) {
        knownOrders.current.add(order.id_order)
        orderStatus.current.set(order.id_order, order.status)
      }
      seeded.current = true
      return
    }

    // ── Order-enter detection ──────────────────────────────
    const newOrders = orders.filter(o => !knownOrders.current.has(o.id_order))
    for (const o of newOrders) knownOrders.current.add(o.id_order)
    // A single arrival is a live order → cue. A burst is a bulk reload
    // (hydration / tenant switch) → seed silently.
    if (newOrders.length === 1) {
      playOrderEnter()
    }

    // ── Order-complete detection (any order → 'fulfilled') ─────────
    let orderCompleted = false
    for (const order of orders) {
      const prev = orderStatus.current.get(order.id_order)
      if (prev !== order.status) {
        orderStatus.current.set(order.id_order, order.status)
        if (prev != null && prev !== 'fulfilled' && order.status === 'fulfilled') {
          orderCompleted = true
        }
      }
    }
    // One cue per tick even if several orders complete together — keeps the
    // confirmation crisp instead of overlapping into noise.
    if (orderCompleted && newOrders.length <= 1) {
      playOrderComplete()
    }
  }, [state.tables, playOrderComplete, playOrderEnter])
}
